import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FloppyDisk, PaperPlaneTilt, ShieldCheck, Key } from "@phosphor-icons/react";
import IntegrationsCanary from "@/components/IntegrationsCanary";
import PlaybooksAuthoring from "@/components/PlaybooksAuthoring";
import OutlookStatus from "@/components/OutlookStatus";
import PWAInstallButton from "@/components/PWAInstallButton";
import FontScaleCard from "@/components/FontScaleCard";
import DensityCard from "@/components/DensityCard";
import ThemeToggleCard from "@/components/ThemeToggleCard";
import WonChimeCard from "@/components/WonChimeCard";
import BounceAlertsBadge from "@/components/BounceAlertsBadge";
import NamePreferences from "@/components/NamePreferences";

export default function Settings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyLog, setVerifyLog] = useState([]); // history of verification results, newest first
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/firm/settings").then((r) => setS(r.data)),
      api.get("/auth/me").then((r) => { setTestEmail(r.data.email || ""); setVerifyEmail(r.data.email || ""); }),
    ]);
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/firm/settings", s);
      setS(data);
      toast.success("Firm settings saved");
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const verifySender = async () => {
    if (!verifyEmail) return toast.error("Recipient email required");
    setVerifying(true);
    try {
      const { data } = await api.post("/firm/settings/verify-sender", { recipient_email: verifyEmail });
      const entry = { ...data, when: new Date().toISOString() };
      setVerifyLog((prev) => [entry, ...prev].slice(0, 5));
      toast.success(`Verification email dispatched to ${data.recipient}`, {
        description: `email_id ${data.email_id || "(none)"} · reply-to ${data.reply_to || "(unset)"}. If it doesn't land in your inbox within ~90s, check Junk/Other.`,
        duration: 12000,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verify failed");
    } finally { setVerifying(false); }
  };

  const sendTest = async () => {
    if (!testEmail) return toast.error("Recipient email required");
    setSending(true);
    try {
      const { data } = await api.post("/firm/settings/test-send", { recipient_email: testEmail });
      toast.success(`Test invoice sent to ${data.recipient}`);
      setTestOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally { setSending(false); }
  };

  const changePassword = async () => {
    if (!pwdCurrent || !pwdNew) return toast.error("Current and new passwords are required");
    if (pwdNew.length < 8) return toast.error("New password must be at least 8 characters");
    if (pwdNew !== pwdConfirm) return toast.error("New password and confirmation do not match");
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwdCurrent, new_password: pwdNew });
      toast.success("Password updated. The new password is now active for this account.");
      setPwdOpen(false);
      setPwdCurrent(""); setPwdNew(""); setPwdConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Password change failed");
    } finally {
      setPwdBusy(false);
    }
  };

  if (!s) return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;

  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <PageShell size="content">
      <div className="mb-6">
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary">Configuration</div>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Firm settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Used on invoices, proposals, and public-facing documents.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-border bg-card p-6 md:col-span-2">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">Identity</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Firm name</Label>
              <Input data-testid="firm-name" value={s.name || ""} onChange={(e) => set("name", e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Tagline</Label>
              <Input value={s.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Address</Label>
              <Textarea rows={2} value={s.address || ""} onChange={(e) => set("address", e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Email</Label>
              <Input value={s.email || ""} onChange={(e) => set("email", e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Phone</Label>
              <Input value={s.phone || ""} onChange={(e) => set("phone", e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Website</Label>
              <Input value={s.website || ""} onChange={(e) => set("website", e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Public app origin</Label>
              <Input
                value={s.public_origin || ""}
                onChange={(e) => set("public_origin", e.target.value)}
                placeholder="https://app.yadidadvisory.com"
                className="rounded-none bg-secondary/40 mt-1"
                data-testid="firm-public-origin"
              />
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1.5">
                Emailed invoice & brief links use this exact origin. Set it to your production custom domain so recipient clicks always land on the correct environment.
              </div>
            </div>
          </div>
        </div>

        <ThemeToggleCard />
        <FontScaleCard />
        <DensityCard />
        <WonChimeCard />

        <div className="border border-border bg-card p-6 md:col-span-2" data-testid="deliverability-card">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">Sender deliverability</div>

          {/*
            Deliverability health lives inline with the sender config so
            partners see "who's your sender + how is that sender doing"
            side-by-side. The BounceAlertsBadge preserves its click-to-open
            popover behavior (Resend bounce/complaint alerts + Mark all seen)
            and its 30s polling — it just moved from the sidebar into the
            settings surface that already owns email delivery config.
          */}
          <div className="mb-4 pb-4 border-b border-border/60">
            <div className="text-[0.6875rem] uppercase font-mono tracking-widest text-primary mb-2">Delivery health</div>
            <p className="text-xs text-muted-foreground mb-3">
              Live status of your outbound email — bounces and spam complaints from Resend show up here so you can react before they hurt sender reputation.
            </p>
            <div className="max-w-sm">
              <BounceAlertsBadge />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Custom sender email</Label>
              <Input data-testid="firm-sender-from" placeholder="hi@yadidadvisory.com" value={s.sender_from_email || ""} onChange={(e) => set("sender_from_email", e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
              <p className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1">Leave blank to use the shared Emergent-managed sender</p>
            </div>
            <div className="md:col-span-2 border-l border-border/60 pl-4">
              <div className="text-[0.6875rem] uppercase font-mono tracking-widest text-primary mb-1">To go live from your own domain</div>
              <ol className="text-xs text-muted-foreground leading-relaxed list-decimal ml-4 space-y-1">
                <li>Add your domain (e.g. <span className="font-mono text-foreground">yadidadvisory.com</span>) in the Emergent → Resend integration console</li>
                <li>Publish the SPF, DKIM &amp; DMARC DNS records Resend provides — verification usually takes a few minutes</li>
                <li>Come back here, drop <span className="font-mono text-foreground">hi@yadidadvisory.com</span> in above, save, and hit <span className="text-primary">Verify sender</span> to confirm inbox delivery</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="border border-border bg-card p-6">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">Billing</div>
          <div className="space-y-3">
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Currency</Label>
              <Input value={s.currency || "USD"} onChange={(e) => set("currency", e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Default hourly rate ($)</Label>
              <Input type="number" value={s.default_hourly_rate || 250} onChange={(e) => set("default_hourly_rate", parseFloat(e.target.value) || 250)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Payment terms (days)</Label>
              <Input type="number" value={s.payment_terms_days || 30} onChange={(e) => set("payment_terms_days", parseInt(e.target.value) || 30)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Tax rate (%)</Label>
              <Input type="number" step="0.001" value={s.tax_rate || 0} onChange={(e) => set("tax_rate", parseFloat(e.target.value) || 0)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
          </div>
        </div>

        <div className="border border-border bg-card p-6">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">Invoice footer</div>
          <Textarea rows={6} value={s.invoice_footer || ""} onChange={(e) => set("invoice_footer", e.target.value)} placeholder="Payment terms, bank details, thank-you notes…" className="rounded-none bg-secondary/40" />
        </div>

        <IntegrationsCanary />
        <div className="md:col-span-2">
          <NamePreferences />
        </div>
        <PWAInstallButton />
        <OutlookStatus />
        <PlaybooksAuthoring />
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <Button data-testid="firm-save" onClick={save} disabled={busy} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
          <FloppyDisk size={14} weight="bold" className="mr-2" /> {busy ? "Saving…" : "Save changes"}
        </Button>
        <Button data-testid="firm-test-send-btn" onClick={() => setTestOpen(true)} variant="outline" className="rounded-none border-primary/60 text-primary hover:bg-primary/10">
          <PaperPlaneTilt size={14} weight="bold" className="mr-2" /> Send test invoice
        </Button>
        <Button data-testid="firm-verify-sender-btn" onClick={() => setVerifyOpen(true)} variant="outline" className="rounded-none border-primary/60 text-primary hover:bg-primary/10">
          <ShieldCheck size={14} weight="bold" className="mr-2" /> Verify sender
        </Button>
        <Button data-testid="change-password-btn" onClick={() => setPwdOpen(true)} variant="outline" className="rounded-none border-primary/60 text-primary hover:bg-primary/10">
          <Key size={14} weight="bold" className="mr-2" /> Change password
        </Button>
        <span className="text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground">Smoke-test branding, layout, or inbox deliverability</span>
      </div>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="rounded-none bg-card" data-testid="test-send-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <PaperPlaneTilt size={16} weight="fill" className="text-primary" /> Send test invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Generates a PREVIEW invoice PDF (Acme Manufacturing · Operating Model Redesign, sample line items) using your current firm settings and emails it — with the PDF attached — to the address below. No data is written to the invoices table.</p>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Recipient email</Label>
              <Input data-testid="test-send-recipient" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="test-send-submit" onClick={sendTest} disabled={sending} className="rounded-none bg-primary text-primary-foreground">
              {sending ? "Sending…" : "Send test invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent className="rounded-none bg-card" data-testid="verify-sender-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ShieldCheck size={16} weight="fill" className="text-primary" /> Verify sender deliverability
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Fires a one-line test email from your firm's configured sender to the address below and returns the Resend <span className="font-mono text-primary">email_id</span> so you can confirm delivery — or trace it in your inbox if it lands in Junk/Other.
            </p>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Recipient email</Label>
              <Input data-testid="verify-sender-recipient" type="email" value={verifyEmail} onChange={(e) => setVerifyEmail(e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
              <p className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1">Reply-to on the email = firm email in Settings (currently: {s?.email || "unset"})</p>
            </div>
            {verifyLog.length > 0 && (
              <div className="pt-2">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Recent verifications</div>
                <ul className="text-xs font-mono space-y-1 border-l-2 border-primary/40 pl-3" data-testid="verify-sender-log">
                  {verifyLog.map((r, i) => (
                    <li key={r.email_id || `${r.recipient}-${r.sent_at}` || i} className="text-muted-foreground">
                      <span className="text-foreground">{r.recipient}</span> · id {String(r.email_id || "").slice(0,8) || "—"} · reply-to {r.reply_to || "unset"} · {r.sent_at}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="verify-sender-submit" onClick={verifySender} disabled={verifying} className="rounded-none bg-primary text-primary-foreground">
              {verifying ? "Sending…" : "Send verification email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="rounded-none bg-card" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Key size={16} weight="fill" className="text-primary" /> Change password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Rotate the password on this account. The current password is required so a stolen session can't silently lock you out.</p>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Current password</Label>
              <Input data-testid="pwd-current" type="password" autoComplete="current-password" value={pwdCurrent} onChange={(e) => setPwdCurrent(e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">New password (min 8 chars)</Label>
              <Input data-testid="pwd-new" type="password" autoComplete="new-password" value={pwdNew} onChange={(e) => setPwdNew(e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Confirm new password</Label>
              <Input data-testid="pwd-confirm" type="password" autoComplete="new-password" value={pwdConfirm} onChange={(e) => setPwdConfirm(e.target.value)} className="rounded-none bg-secondary/40 mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="pwd-submit" onClick={changePassword} disabled={pwdBusy} className="rounded-none bg-primary text-primary-foreground">
              {pwdBusy ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
